import { queryString } from '@shared/hooks/recoil-query';
import { api_ } from '@shared/plugIn/axios';
import { LowerRole } from '@shared/types';

import { GiftStoreRole } from '../giftOrder/giftOrder.interface';
import {
  GiftDeliveryAddressDto,
  GiftDeliveryForAdmin,
  GiftDeliveryForStore,
  GiftDeliveryPublicResponse,
  GiftDeliveryShareChannel,
  GiftDeliveryShareStatus,
  GiftDeliveryStatus,
} from './giftDelivery.interface';

// code로 배송 조회 (Public)
export const getGiftDeliveryByCode = async (
  code: string,
): Promise<GiftDeliveryPublicResponse> => {
  const { data } = await api_.get<GiftDeliveryPublicResponse>(
    `/public/gift-delivery/code/${code}`,
  );
  return data;
};

// id로 배송 조회 (Public)
export const getGiftDeliveryById = async (
  id: string,
): Promise<GiftDeliveryPublicResponse> => {
  const { data } = await api_.get<GiftDeliveryPublicResponse>(
    `/public/gift-delivery/${id}`,
  );
  return data;
};

/**
 * 공유 처리 (NotShared -> Shared)
 *
 * shareChannel을 함께 보내면 백엔드가 lastSharedChannel을 갱신하고
 * shareHistory에도 기록합니다. 카카오톡은 웹훅으로도 자동 보정됩니다.
 */
export const shareGiftDelivery = async (
  id: string,
  role: GiftStoreRole,
  shareChannel?: GiftDeliveryShareChannel,
): Promise<GiftDeliveryForStore> => {
  const { data } = await api_.post<GiftDeliveryForStore>(
    `/${role}/gift-delivery/share/${id}`,
    shareChannel ? { shareChannel } : {},
  );
  return data;
};

// 공유 취소 (Shared -> NotShared)
export const unshareGiftDelivery = async (
  id: string,
  role: GiftStoreRole,
): Promise<GiftDeliveryForStore> => {
  const { data } = await api_.post<GiftDeliveryForStore>(
    `/${role}/gift-delivery/unshare/${id}`,
  );
  return data;
};

// 전달 확인 (confirmedAt 기록 + memo optional)
export const confirmDelivery = async (
  id: string,
  role: GiftStoreRole,
  memo?: string,
): Promise<GiftDeliveryForStore> => {
  const { data } = await api_.post<GiftDeliveryForStore>(
    `/${role}/gift-delivery/confirm-share/${id}`,
    memo ? { memo } : {},
  );
  return data;
};

/**
 * 카카오톡 회수(잘못 보낸) 케이스에서 배송형 답례품의 수령자 번호를 잠그기 위한 엔드포인트.
 * 백엔드는 `additionalRecipientPhone` 필드를 업데이트하며 (`memo` 동봉 시 메모도 함께
 * 저장), 이후 redeem(배송지 입력) 시 이 번호로만 본인 인증을 허용해 다른 수신자가 받지
 * 못하도록 막는다.
 */
export const updateDeliveryRestrictedPhone = async (
  id: string,
  phoneNumber: string,
  role: GiftStoreRole,
  memo?: string,
): Promise<GiftDeliveryForStore> => {
  const { data } = await api_.patch<GiftDeliveryForStore>(
    `/${role}/gift-delivery/restricted-phone/${id}`,
    { phoneNumber, ...(memo ? { memo } : {}) },
  );
  return data;
};

// 조회 처리 (Shared -> Viewed)
export const viewGiftDelivery = async (
  id: string,
): Promise<GiftDeliveryPublicResponse> => {
  const { data } = await api_.post<GiftDeliveryPublicResponse>(
    `/public/gift-delivery/view/${id}`,
  );
  return data;
};

// 배송지 입력 (Individual 전용)
export const submitGiftDeliveryAddress = async (
  id: string,
  dto: GiftDeliveryAddressDto,
): Promise<GiftDeliveryPublicResponse> => {
  const { data } = await api_.post<GiftDeliveryPublicResponse>(
    `/public/gift-delivery/submit-address/${id}`,
    dto,
  );
  return data;
};

// 배송지 수정 (Public, WaitingAddress/AddressSubmitted 상태에서만 가능)
export const updateGiftDeliveryAddress = async (
  id: string,
  dto: GiftDeliveryAddressDto,
): Promise<GiftDeliveryPublicResponse> => {
  const { data } = await api_.patch<GiftDeliveryPublicResponse>(
    `/public/gift-delivery/update-address/${id}`,
    dto,
  );
  return data;
};

// 수령인 정보 수정 (Admin)
export const updateGiftDeliveryRecipient = async (
  id: string,
  recipient: {
    name?: string;
    phoneNumber?: string;
    address?: string;
    addressDetail?: string;
    postalCode?: string;
  },
  role: LowerRole.Admin | LowerRole.BugoAgencyAdmin = LowerRole.Admin,
): Promise<GiftDeliveryForAdmin> => {
  const { data } = await api_.patch<GiftDeliveryForAdmin>(
    `/${role}/gift-delivery/${id}`,
    { recipient },
  );
  return data;
};

// ===== Admin API =====

export interface UpdateDeliveryStatusDto {
  deliveryStatus: GiftDeliveryStatus;
  shipping?: {
    trackingNumber?: string;
    carrier?: string;
    shippedAt?: string;
    deliveredAt?: string;
    deliveredImage?: string;
  };
}

// 배송 상태 변경 (Admin) - 3단계 지원
// 1. AddressSubmitted → Preparing (발주)
// 2. Preparing → Shipping (송장 입력: trackingNumber, carrier, shippedAt)
// 3. Shipping → Delivered (배송완료: deliveredAt, deliveredImage)
export const updateGiftDeliveryStatus = async (
  id: string,
  status: GiftDeliveryStatus,
  shipping?: {
    trackingNumber?: string;
    carrier?: string;
    shippedAt?: string;
    deliveredAt?: string;
    deliveredImage?: string;
  },
  role:
    | LowerRole.Admin
    | LowerRole.BugoAgencyAdmin
    | LowerRole.GiftBrandSeller = LowerRole.Admin,
): Promise<GiftDeliveryForAdmin> => {
  // shipping은 dot-path('shipping.trackingNumber' 등)로 보내 부분 업데이트한다.
  // 객체를 통째로({ shipping: {...} }) 보내면 백엔드 findByIdAndUpdate가
  // shipping 서브도큐먼트 전체를 교체해 기존 송장번호/발송일시 등이 사라진다.
  const body: Record<string, unknown> = { deliveryStatus: status };
  if (shipping) {
    for (const [key, value] of Object.entries(shipping)) {
      if (value !== undefined) body[`shipping.${key}`] = value;
    }
  }

  const { data } = await api_.patch<GiftDeliveryForAdmin>(
    `/${role}/gift-delivery/${id}`,
    body,
  );
  return data;
};

// Admin에서 공유 상태를 미공유(NotShared)로 초기화.
// 일괄배송 등으로 공유 개념이 없는데 잘못 Shared/Viewed 처리된 배송을
// 운영자가 수동 정정해 취소 가능 상태로 되돌리는 용도. (resetCouponToNotShared 와 대응)
export const resetDeliveryToNotShared = async (
  id: string,
): Promise<GiftDeliveryForAdmin> => {
  const { data } = await api_.patch<GiftDeliveryForAdmin>(
    `/${LowerRole.Admin}/gift-delivery/${id}`,
    { shareStatus: GiftDeliveryShareStatus.NotShared },
  );
  return data;
};

// ===== 배송관리자 포털 (GiftBrandSeller / Admin) =====
// role=gift-brand-seller → generic CommonController, owningFilter(본인 브랜드)가 강제됨.
// role=admin → /admin/gift-delivery, 스코핑 없이 전 브랜드 주문 조회.
// 둘 다 동일한 filter/populate 구조를 쓰므로 role만 갈아끼운다.
const giftBrandSellerDeliveryPopulate = [
  // admin 조회 시 어느 브랜드 주문인지 구분하기 위해 브랜드명 포함 (브랜드셀러는 본인 1곳)
  { path: 'giftItemBrand', select: 'name' },
  {
    path: 'giftItem',
    populate: [{ path: 'image' }, { path: 'giftItemInfo' }],
  },
  {
    path: 'giftOrder',
    select:
      'receiverName receiverPhoneNumber deliveryMode paymentDetail.purchasedAt bugo',
    populate: [
      {
        path: 'bugo',
        select: 'member',
        populate: [{ path: 'member', select: 'fullName phoneNumber' }],
      },
    ],
  },
  { path: 'shipping.deliveredImage', select: 'url' },
];

export const listGiftBrandSellerDeliveries = async (
  filter: Record<string, unknown> = {},
  sort: Record<string, 1 | -1> = { createdAt: -1 },
  role: LowerRole.Admin | LowerRole.GiftBrandSeller = LowerRole.GiftBrandSeller,
): Promise<GiftDeliveryForAdmin[]> => {
  const { data } = await api_.get<GiftDeliveryForAdmin[]>(
    `/${role}/gift-delivery?${queryString({
      filter,
      populate: giftBrandSellerDeliveryPopulate,
      options: { sort },
    })}`,
  );
  return data;
};

// 상태별 주문 건수 (필터 칩 뱃지용). populate 없이 countDocuments만 수행해 가볍다.
export const countGiftBrandSellerDeliveries = async (
  filter: Record<string, unknown> = {},
  role: LowerRole.Admin | LowerRole.GiftBrandSeller = LowerRole.GiftBrandSeller,
): Promise<number> => {
  const { data } = await api_.get<number>(
    `/${role}/gift-delivery/count?${queryString({ filter })}`,
  );
  return data;
};
